import { NgSwitch, NgSwitchCase } from '@angular/common';
import * as i0 from '@angular/core';
import { InjectionToken, EventEmitter, inject, Component, ViewChild, Output, Injectable, Inject, createComponent, Injector, ApplicationRef, APP_INITIALIZER } from '@angular/core';
import * as i1 from '@angular/service-worker';
import { SwUpdate } from '@angular/service-worker';
import { filter, interval } from 'rxjs';
import { tap, startWith, concatMap } from 'rxjs/operators';
import * as i1$1 from '@rxap/life-cycle';
import { LifeCycleService } from '@rxap/life-cycle';

const UPDATE_AVAILABLE_EVENT = new InjectionToken('UPDATE_AVAILABLE_EVENT');
class ServiceWorkerUpdateDialogComponent {
    constructor() {
        this.closeDialog = new EventEmitter();
        this.updateAvailableEvent = inject(UPDATE_AVAILABLE_EVENT);
    }
    ngAfterViewInit() {
        this.dialog.nativeElement.showModal();
    }
    close() {
        this.dialog.nativeElement.close();
        this.closeDialog.emit();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.0.1", ngImport: i0, type: ServiceWorkerUpdateDialogComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.0.1", type: ServiceWorkerUpdateDialogComponent, isStandalone: true, selector: "rxap-service-worker-update-dialog", outputs: { closeDialog: "closeDialog" }, viewQueries: [{ propertyName: "dialog", first: true, predicate: ["dialog"], descendants: true, static: true }], ngImport: i0, template: "<dialog #dialog\n        class=\"max-w-6xl rounded overflow-auto drop-shadow-2xl dark:bg-black dark:text-white flex flex-col gap-3 p-6\">\n  <h2 class=\"text-2xl font-bold\" i18n>A new application version is available</h2>\n  <ng-container [ngSwitch]=\"$any(updateAvailableEvent).type\">\n    <table *ngSwitchCase=\"'VERSION_DETECTED'\" class=\"table-auto\">\n      <tr>\n        <th i18n>Version hash</th>\n        <td>{{$any(updateAvailableEvent)['version'].hash}}</td>\n      </tr>\n    </table>\n    <table *ngSwitchCase=\"'VERSION_INSTALLATION_FAILED'\" class=\"table-auto\">\n      <tr>\n        <th i18n>Version hash</th>\n        <td>{{$any(updateAvailableEvent)['version'].hash}}</td>\n      </tr>\n      <tr>\n        <th i18n>Version installation error</th>\n        <td>{{$any(updateAvailableEvent)['error']}}</td>\n      </tr>\n    </table>\n    <table *ngSwitchCase=\"'VERSION_READY'\" class=\"table-auto\">\n      <tr>\n        <th i18n>Current version hash</th>\n        <td>{{$any(updateAvailableEvent)['currentVersion'].hash}}</td>\n      </tr>\n      <tr>\n        <th i18n>Last version hash</th>\n        <td>{{$any(updateAvailableEvent)['latestVersion'].hash}}</td>\n      </tr>\n    </table>\n    <table *ngSwitchCase=\"'NoNewVersionDetectedEvent'\" class=\"table-auto\">\n      <tr>\n        <th i18n>Version hash</th>\n        <td>{{$any(updateAvailableEvent)['version'].hash}}</td>\n      </tr>\n    </table>\n  </ng-container>\n  <button (click)=\"close()\" class=\"px-4 py-2 rounded bg-primary-500\" i18n>Update</button>\n</dialog>\n", styles: [""], dependencies: [{ kind: "directive", type: NgSwitch, selector: "[ngSwitch]", inputs: ["ngSwitch"] }, { kind: "directive", type: NgSwitchCase, selector: "[ngSwitchCase]", inputs: ["ngSwitchCase"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.0.1", ngImport: i0, type: ServiceWorkerUpdateDialogComponent, decorators: [{
            type: Component,
            args: [{ selector: 'rxap-service-worker-update-dialog', standalone: true, imports: [
                        NgSwitch,
                        NgSwitchCase,
                    ], template: "<dialog #dialog\n        class=\"max-w-6xl rounded overflow-auto drop-shadow-2xl dark:bg-black dark:text-white flex flex-col gap-3 p-6\">\n  <h2 class=\"text-2xl font-bold\" i18n>A new application version is available</h2>\n  <ng-container [ngSwitch]=\"$any(updateAvailableEvent).type\">\n    <table *ngSwitchCase=\"'VERSION_DETECTED'\" class=\"table-auto\">\n      <tr>\n        <th i18n>Version hash</th>\n        <td>{{$any(updateAvailableEvent)['version'].hash}}</td>\n      </tr>\n    </table>\n    <table *ngSwitchCase=\"'VERSION_INSTALLATION_FAILED'\" class=\"table-auto\">\n      <tr>\n        <th i18n>Version hash</th>\n        <td>{{$any(updateAvailableEvent)['version'].hash}}</td>\n      </tr>\n      <tr>\n        <th i18n>Version installation error</th>\n        <td>{{$any(updateAvailableEvent)['error']}}</td>\n      </tr>\n    </table>\n    <table *ngSwitchCase=\"'VERSION_READY'\" class=\"table-auto\">\n      <tr>\n        <th i18n>Current version hash</th>\n        <td>{{$any(updateAvailableEvent)['currentVersion'].hash}}</td>\n      </tr>\n      <tr>\n        <th i18n>Last version hash</th>\n        <td>{{$any(updateAvailableEvent)['latestVersion'].hash}}</td>\n      </tr>\n    </table>\n    <table *ngSwitchCase=\"'NoNewVersionDetectedEvent'\" class=\"table-auto\">\n      <tr>\n        <th i18n>Version hash</th>\n        <td>{{$any(updateAvailableEvent)['version'].hash}}</td>\n      </tr>\n    </table>\n  </ng-container>\n  <button (click)=\"close()\" class=\"px-4 py-2 rounded bg-primary-500\" i18n>Update</button>\n</dialog>\n" }]
        }], propDecorators: { dialog: [{
                type: ViewChild,
                args: ['dialog', { static: true }]
            }], closeDialog: [{
                type: Output
            }] } });

class AutoUpdateService {
    constructor(updates) {
        this.updates = updates;
    }
    start() {
        console.debug('start auto update service');
        this.updates.versionUpdates
            .pipe(filter(event => event.type === 'VERSION_READY'), tap(() => console.log('start auto app update')), tap(() => this.updates.activateUpdate().then(() => {
            console.log('auto app update completed. Reload app.');
            document.location.reload();
        })))
            .subscribe();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.0.1", ngImport: i0, type: AutoUpdateService, deps: [{ token: SwUpdate }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.0.1", ngImport: i0, type: AutoUpdateService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.0.1", ngImport: i0, type: AutoUpdateService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: () => [{ type: i1.SwUpdate, decorators: [{
                    type: Inject,
                    args: [SwUpdate]
                }] }] });

class CheckForUpdateService {
    constructor(lifeCycle, updates) {
        this.lifeCycle = lifeCycle;
        this.updates = updates;
        this._started = false;
    }
    start() {
        if (this._started) {
            console.warn('check for update already started');
            return;
        }
        this._started = true;
        if (this.updates.isEnabled) {
            console.debug('start check for update');
            this.updates.checkForUpdate();
            this
                .lifeCycle
                .whenReady(() => interval(15 * 60 * 1000)
                .pipe(startWith(null), tap(() => console.log('check for update')), tap(() => this.updates.checkForUpdate())))
                .subscribe();
        }
        else {
            console.warn('service worker updates are not enabled');
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.0.1", ngImport: i0, type: CheckForUpdateService, deps: [{ token: LifeCycleService }, { token: SwUpdate }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.0.1", ngImport: i0, type: CheckForUpdateService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.0.1", ngImport: i0, type: CheckForUpdateService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: () => [{ type: i1$1.LifeCycleService, decorators: [{
                    type: Inject,
                    args: [LifeCycleService]
                }] }, { type: i1.SwUpdate, decorators: [{
                    type: Inject,
                    args: [SwUpdate]
                }] }] });

class DialogUpdateService {
    constructor(applicationRef, updates) {
        this.applicationRef = applicationRef;
        this.updates = updates;
    }
    start() {
        console.debug('start dialog update service');
        this.updates.versionUpdates
            .pipe(filter(event => event.type === 'VERSION_READY'), concatMap((event) => this.openUpdateDialog(event)), tap(() => console.log('start app update')), tap(() => this.updates.activateUpdate().then(() => {
            console.log('app update completed. Reload app.');
            document.location.reload();
        })))
            .subscribe();
    }
    openUpdateDialog(event) {
        console.debug('open update dialog');
        const body = document.getElementsByTagName('body')[0];
        const div = document.createElement('div');
        body.appendChild(div);
        const componentRef = createComponent(ServiceWorkerUpdateDialogComponent, {
            hostElement: div,
            environmentInjector: this.applicationRef.injector,
            elementInjector: Injector.create({
                providers: [
                    {
                        provide: UPDATE_AVAILABLE_EVENT,
                        useValue: event,
                    },
                ],
            }),
        });
        this.applicationRef.attachView(componentRef.hostView);
        return componentRef.instance.closeDialog.pipe(tap(() => {
            componentRef.destroy();
            body.removeChild(div);
            div.remove();
        }));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.0.1", ngImport: i0, type: DialogUpdateService, deps: [{ token: ApplicationRef }, { token: SwUpdate }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.0.1", ngImport: i0, type: DialogUpdateService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.0.1", ngImport: i0, type: DialogUpdateService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: () => [{ type: i0.ApplicationRef, decorators: [{
                    type: Inject,
                    args: [ApplicationRef]
                }] }, { type: i1.SwUpdate, decorators: [{
                    type: Inject,
                    args: [SwUpdate]
                }] }] });

class LogUpdateService {
    constructor(updates) {
        this.updates = updates;
    }
    start() {
        console.debug('start log update');
        this.updates.versionUpdates.pipe(filter(event => event.type !== 'NO_NEW_VERSION_DETECTED')).subscribe(event => {
            console.log('version update event', event);
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.0.1", ngImport: i0, type: LogUpdateService, deps: [{ token: i1.SwUpdate }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.0.1", ngImport: i0, type: LogUpdateService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.0.1", ngImport: i0, type: LogUpdateService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: () => [{ type: i1.SwUpdate }] });

function ProvideServiceWorkerUpdater(...providers) {
    return [
        {
            provide: APP_INITIALIZER,
            multi: true,
            useFactory: (checkForUpdate) => () => checkForUpdate.start(),
            deps: [CheckForUpdateService],
        },
        ...providers,
    ];
}
function withLogUpdater() {
    return {
        provide: APP_INITIALIZER,
        multi: true,
        useFactory: (logUpdateService) => () => logUpdateService.start(),
        deps: [LogUpdateService],
    };
}
function withDialogUpdater() {
    return {
        provide: APP_INITIALIZER,
        multi: true,
        useFactory: (dus) => () => dus.start(),
        deps: [DialogUpdateService],
    };
}
function withAutoUpdater() {
    return {
        provide: APP_INITIALIZER,
        multi: true,
        useFactory: (aus) => () => aus.start(),
        deps: [AutoUpdateService],
    };
}

function UnregisterServiceWorker(environment) {
    if (!environment.serviceWorker) {
        navigator?.serviceWorker?.getRegistrations().then(async (registrations) => {
            await Promise.all(registrations.map((registration) => {
                console.log('unregister service worker: ' + registration.scope);
                return registration.unregister();
            }));
            if (registrations.length) {
                const response = confirm('All dangling service worker are unregistered. Should the application be reloaded to complete the migration');
                if (response) {
                    location.reload();
                }
            }
        });
    }
}

// region service-worker-update-dialog
// endregion

/**
 * Generated bundle index. Do not edit.
 */

export { AutoUpdateService, CheckForUpdateService, DialogUpdateService, LogUpdateService, ProvideServiceWorkerUpdater, ServiceWorkerUpdateDialogComponent, UPDATE_AVAILABLE_EVENT, UnregisterServiceWorker, withAutoUpdater, withDialogUpdater, withLogUpdater };
